import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Send, CheckCircle, AlertCircle, Upload } from 'lucide-react';

const exteriorPOIs = [
  { key: 'lights', en: 'Lights & Fixtures', es: 'Luces y Accesorios' },
  { key: 'security', en: 'Security', es: 'Seguridad' },
  { key: 'painting', en: 'Painting', es: 'Pintura' },
  { key: 'cleanliness', en: 'Cleanliness / Vandalism', es: 'Limpieza / Vandalismo' },
  { key: 'signage', en: 'Bank Signage', es: 'Señalización del Banco' },
  { key: 'car_stops', en: 'Car Stops', es: 'Topes para Autos' },
  { key: 'obstructions', en: 'Obstructions', es: 'Obstrucciones' },
  { key: 'landscape', en: 'Landscape', es: 'Paisajismo' },
  { key: 'exits', en: 'All Exits', es: 'Todas las Salidas' },
  { key: 'door_locks', en: 'Door Locks', es: 'Cerraduras' },
  { key: 'gutters', en: 'Gutters & Building Drains', es: 'Canalones y Drenajes' },
  { key: 'asphalt', en: 'Asphalt & Floor Drains', es: 'Asfalto y Drenajes' },
  { key: 'atm', en: 'ATM', es: 'Cajero Automático' },
  { key: 'windows', en: 'Windows', es: 'Ventanas' },
  { key: 'timers', en: 'Exterior Timer(s)', es: 'Temporizadores' },
  { key: 'compactor', en: 'Compactor(s)', es: 'Compactadores' },
  { key: 'night_dep', en: 'Night Depository', es: 'Depósito Nocturno' }
];

const interiorPOIs = [
  { key: 'lights', en: 'Lights & Fixtures', es: 'Luces y Accesorios' },
  { key: 'egress', en: 'Exit/Egress Lighting', es: 'Iluminación de Salida' },
  { key: 'ceiling', en: 'Ceiling Tiles', es: 'Losetas del Techo' },
  { key: 'flooring', en: 'Flooring & Carpeting', es: 'Pisos y Alfombras' },
  { key: 'doors', en: 'Doors/Locks/Fasteners', es: 'Puertas/Cerraduras' },
  { key: 'ac_closet', en: 'A/C Closet / Mechanical Rooms', es: 'Cuarto de A/C' },
  { key: 'phone', en: 'Phone Room', es: 'Cuarto de Teléfonos' },
  { key: 'plumbing', en: 'Plumbing (General)', es: 'Plomería' },
  { key: 'kitchen', en: 'Kitchen Appliances', es: 'Electrodomésticos' },
  { key: 'exhaust', en: 'Exhaust Fans', es: 'Ventiladores' },
  { key: 'panic', en: 'Panic Bars', es: 'Barras Anti-pánico' },
  { key: 'painting', en: 'Painting', es: 'Pintura' },
  { key: 'cleanliness', en: 'Cleanliness', es: 'Limpieza' },
  { key: 'timers', en: 'Timers', es: 'Temporizadores' },
  { key: 'filters', en: 'A/C Filters', es: 'Filtros A/C' },
  { key: 'windows', en: 'Windows', es: 'Ventanas' },
  { key: 'fire_ext', en: 'Fire Extinguishers (Date)', es: 'Extintores (Fecha)' },
  { key: 'elevator', en: 'Elevator(s)', es: 'Ascensor(es)' },
  { key: 'offices', en: 'Offices (Bank)', es: 'Oficinas (Banco)' }
];

const safetyPOIs = [
  { key: 'breaker', en: 'Breaker Panels', es: 'Paneles de Interruptores' },
  { key: 'electrical', en: 'Electrical Rooms', es: 'Cuartos Eléctricos' },
  { key: 'water_heater', en: 'Water Heater', es: 'Calentador de Agua' },
  { key: 'janitor', en: 'Janitor Closet(s)', es: 'Closets de Conserje' },
  { key: 'storage', en: 'Storage Areas', es: 'Áreas de Almacenamiento' },
  { key: 'security_sys', en: 'Security Systems', es: 'Sistemas de Seguridad' }
];

const BankPMInspectionForm = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  
  const [formData, setFormData] = useState({
    bank_customer_id: '',
    branch_location_id: '',
    date_of_inspection: new Date().toISOString().split('T')[0],
    inspector_name: '',
    general_notes: '',
    inspector_observations: '',
    forward_to_operations: false
  });

  const [inspectionData, setInspectionData] = useState({
    exterior: {},
    interior: {},
    safety: {}
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBankCustomers();
  }, []);

  useEffect(() => {
    if (formData.bank_customer_id) {
      fetchBranches(formData.bank_customer_id);
    }
  }, [formData.bank_customer_id]);

  const fetchBankCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter for bank/financial customers
      setCustomers(response.data.filter(c => 
        (c.type === 'financial' || c.type === 'bank') && c.status === 'active'
      ));
    } catch (error) {
      toast.error('Failed to fetch bank customers');
    }
  };

  const fetchBranches = async (customerId) => {
    try {
      const response = await axios.get(`${API}/service-locations/by-customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(response.data);
    } catch (error) {
      console.error('Failed to fetch branches');
      setBranches([]);
    }
  };

  const handleInspectionChange = (category, itemKey, field, value) => {
    setInspectionData({
      ...inspectionData,
      [category]: {
        ...inspectionData[category],
        [itemKey]: {
          ...inspectionData[category][itemKey],
          [field]: value
        }
      }
    });
  };

  const getInspectionStatus = (category, itemKey) => {
    return inspectionData[category]?.[itemKey]?.status || 'satisfactory';
  };

  const getInspectionDescription = (category, itemKey) => {
    return inspectionData[category]?.[itemKey]?.description || '';
  };

  const buildInspectionItems = (category, items) => {
    return items.map(item => ({
      category: category,
      item: `${item.en} / ${item.es}`,
      status: inspectionData[category]?.[item.key]?.status || 'satisfactory',
      description: inspectionData[category]?.[item.key]?.description || null,
      photo: inspectionData[category]?.[item.key]?.photo || null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let hasIssues = false;
    Object.entries(inspectionData).forEach(([category, items]) => {
      Object.entries(items).forEach(([key, item]) => {
        if (item.status === 'needs_attention' && !item.description) {
          hasIssues = true;
        }
      });
    });

    if (hasIssues) {
      toast.error('Please provide descriptions for all items marked "Needs Attention"');
      return;
    }

    setLoading(true);

    try {
      // Get selected customer and branch details
      const selectedCustomer = customers.find(c => c.id === formData.bank_customer_id);
      const selectedBranch = branches.find(b => b.id === formData.branch_location_id);
      
      const pmPayload = {
        customer_id: formData.bank_customer_id,
        service_location_id: formData.branch_location_id,
        bank_name: selectedCustomer?.name || '',
        branch_location: selectedBranch?.location_name || '',
        date_of_inspection: formData.date_of_inspection,
        inspector_name: formData.inspector_name,
        exterior_items: buildInspectionItems('exterior', exteriorPOIs),
        interior_items: buildInspectionItems('interior', interiorPOIs),
        safety_operational_items: buildInspectionItems('safety', safetyPOIs),
        general_notes: formData.general_notes || null,
        inspector_observations: formData.inspector_observations || null,
        additional_photos: [],
        forward_to_operations: formData.forward_to_operations
      };

      await axios.post(`${API}/bank-pm-inspections`, pmPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Bank PM inspection submitted successfully!');
      navigate('/field/bank-pm-inspections');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit inspection');
    } finally {
      setLoading(false);
    }
  };

  const renderInspectionCategory = (title, titleEs, category, items) => (
    <div className="space-y-4">
      <h3 className="font-semibold text-lg border-b pb-2">{title} / {titleEs}</h3>
      {items.map((item) => (
        <div key={item.key} className="p-4 bg-slate-50 rounded-lg border">
          <div className="flex items-start justify-between mb-3">
            <Label className="font-medium">{item.en} / {item.es}</Label>
            <RadioGroup
              value={getInspectionStatus(category, item.key)}
              onValueChange={(value) => handleInspectionChange(category, item.key, 'status', value)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="satisfactory" id={`${category}-${item.key}-sat`} />
                <Label htmlFor={`${category}-${item.key}-sat`} className="font-normal flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Satisfactory</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="needs_attention" id={`${category}-${item.key}-att`} />
                <Label htmlFor={`${category}-${item.key}-att`} className="font-normal flex items-center gap-1">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  <span className="text-sm">Needs Attention</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {getInspectionStatus(category, item.key) === 'needs_attention' && (
            <div className="mt-3 space-y-3 pl-4 border-l-2 border-orange-400">
              <div>
                <Label className="text-sm text-orange-700">Description Required *</Label>
                <Textarea
                  value={getInspectionDescription(category, item.key)}
                  onChange={(e) => handleInspectionChange(category, item.key, 'description', e.target.value)}
                  placeholder="Describe the issue"
                  rows={2}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">
                  <Upload className="h-4 w-4 inline mr-1" />
                  Photo Upload (Required)
                </Label>
                <p className="text-xs text-slate-500">Photo upload placeholder</p>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <Button variant="outline" onClick={() => navigate('/field')} className="mb-4 bg-white">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back / Volver
      </Button>

      <Card className="shadow-xl">
        <div className="h-4" style={{ backgroundColor: '#D3D3D3' }} />
        <CardHeader>
          <CardTitle className="text-2xl">
            PREVENTIVE MAINTENANCE – BANKS
          </CardTitle>
          <CardTitle className="text-xl text-slate-600">
            MANTENIMIENTO PREVENTIVO – BANCOS
          </CardTitle>
          <CardDescription>
            Operational, structural, safety & aesthetic PM inspection
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <h3 className="font-semibold">Bank Branch Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank">Bank Name / Nombre del Banco *</Label>
                  <select
                    id="bank"
                    className="w-full border rounded-md px-3 py-2"
                    value={formData.bank_customer_id}
                    onChange={(e) => setFormData({ ...formData, bank_customer_id: e.target.value, branch_location_id: '' })}
                    required
                    data-testid="pm-bank-select"
                  >
                    <option value="">Select Bank</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>{customer.name}</option>
                    ))}
                  </select>
                </div>

                {formData.bank_customer_id && (
                  <div className="space-y-2">
                    <Label htmlFor="branch">Branch Location / Ubicación *</Label>
                    <select
                      id="branch"
                      className="w-full border rounded-md px-3 py-2"
                      value={formData.branch_location_id}
                      onChange={(e) => setFormData({ ...formData, branch_location_id: e.target.value })}
                      required
                      data-testid="pm-branch-select"
                    >
                      <option value="">Select Branch</option>
                      {branches.map(branch => (
                        <option key={branch.id} value={branch.id}>{branch.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="date">Date of Inspection *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date_of_inspection}
                    onChange={(e) => setFormData({ ...formData, date_of_inspection: e.target.value })}
                    required
                    data-testid="pm-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inspector">Inspector Name *</Label>
                  <Input
                    id="inspector"
                    value={formData.inspector_name}
                    onChange={(e) => setFormData({ ...formData, inspector_name: e.target.value })}
                    required
                    data-testid="pm-inspector-input"
                  />
                </div>
              </div>
            </div>

            {formData.branch_location_id && (
              <>
                {renderInspectionCategory('EXTERIOR PM INSPECTION', 'INSPECCIÓN PM EXTERIOR', 'exterior', exteriorPOIs)}
                {renderInspectionCategory('INTERIOR PM INSPECTION', 'INSPECCIÓN PM INTERIOR', 'interior', interiorPOIs)}
                {renderInspectionCategory('SAFETY & OPERATIONAL', 'SEGURIDAD Y OPERACIONAL', 'safety', safetyPOIs)}

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="notes">General Notes / Notas Generales</Label>
                    <Textarea
                      id="notes"
                      value={formData.general_notes}
                      onChange={(e) => setFormData({ ...formData, general_notes: e.target.value })}
                      rows={3}
                      data-testid="pm-notes-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="observations">Inspector Observations / Observaciones del Inspector</Label>
                    <Textarea
                      id="observations"
                      value={formData.inspector_observations}
                      onChange={(e) => setFormData({ ...formData, inspector_observations: e.target.value })}
                      rows={3}
                      data-testid="pm-observations-input"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <Checkbox
                    id="forward"
                    checked={formData.forward_to_operations}
                    onCheckedChange={(checked) => setFormData({ ...formData, forward_to_operations: checked })}
                    data-testid="pm-forward-checkbox"
                  />
                  <Label htmlFor="forward" className="font-medium">
                    Forward to Operations / Enviar a Operaciones
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  style={{ backgroundColor: '#6B9F3E' }}
                  data-testid="pm-submit-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {loading ? 'Submitting...' : 'Submit PM Inspection'}
                </Button>
              </>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default BankPMInspectionForm;
